import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import LabeledInput from '@/pages/newSupported/components/LabeledInput';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { putAuth } from '@/api/ApiCalls';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { openSupplierTaxApprovalDocument } from '@/helpers/openSupplierTaxApprovalDocument';
import { SupplierFullDtoIn, SupplierTaxApprovalStatus } from '@/models/in/SupplierFullDtoIn';
import { UpdateSupplierTaxApprovalDtoOut } from '@/models/out/UpdateSupplierTaxApprovalDtoOut';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';

/**
 * Pull a human-readable error message out of an axios/fetch failure. We want
 * the secretariat to see the backend's actual rejection text (e.g. "Document
 * content is required.") instead of axios's generic "Request failed with
 * status code 400" whenever possible, so that issues can be diagnosed without
 * opening DevTools.
 */
function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (typeof obj.title === 'string' && obj.title.trim()) return obj.title;
      if (typeof obj.detail === 'string' && obj.detail.trim()) return obj.detail;
      if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    }
    const status = err.response?.status;
    return status ? `HTTP ${status}: ${err.message}` : err.message;
  }
  if (err instanceof Error) return err.message;
  return '';
}

const QUERY_URL = 'management/suppliers';

interface EditSupplierTaxWithholdingDialogProps {
  open: boolean;
  onClose: () => void;
  supplier: SupplierFullDtoIn;
  onSuccess: () => void;
}

const formatDateForInput = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Dialog for managing tax-withholding-at-source settings for a supplier:
 *   - percentage to withhold (0-100). 0 is a real configured value, NOT "no
 *     withholding" - it still requires a valid-until date + approval document
 *     and goes through the same approval pipeline as any non-null percentage.
 *     New suppliers default to 0% (set by the backend on create), so the
 *     "no withholding" case (percentage = null) only exists for legacy
 *     suppliers that pre-date the default-0% change.
 *   - approval document
 *   - approval valid-until date
 *
 * Overwrite/replace flow: every field is editable at any time and the save
 * overwrites the previous value on the supplier. This replaces the older
 * Append-only behavior where each field could only be filled in once. The
 * secretariat needs this to renew an expired approval (new valid-until +
 * new document) and to fix mistaken values - we don't want them to have to
 * go to the database for routine corrections.
 *
 * Document upload happens on save (not on file-pick) so the whole change is
 * atomic - the user can change their mind and close without DB writes.
 * When a new document is uploaded it replaces the previous approval document
 * on the supplier; the old NetCoreDocument row + Azure blob stay behind as
 * orphans (cleanup is left to a future maintenance flow).
 */
const EditSupplierTaxWithholdingDialog = ({
  open,
  onClose,
  supplier,
  onSuccess,
}: EditSupplierTaxWithholdingDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = useMemo(() => ({ Authorization: `Bearer ${currentUser?.token}` }), [currentUser?.token]);

  const originalValidUntil = useMemo(
    () => formatDateForInput(supplier.taxApprovalValidUntil),
    [supplier.taxApprovalValidUntil],
  );

  const [percentage, setPercentage] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');
  const [newDocumentFile, setNewDocumentFile] = useState<File | null>(null);
  const [isViewing, setIsViewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPercentage(
      supplier.taxWithholdingPercentage != null ? String(supplier.taxWithholdingPercentage) : '',
    );
    setValidUntil(originalValidUntil);
    setNewDocumentFile(null);
    setError(null);
  }, [open, supplier, originalValidUntil]);

  const parsedPercentage = (() => {
    if (percentage.trim() === '') return null;
    const num = parseFloat(percentage);
    return isNaN(num) ? NaN : num;
  })();

  // Whether the supplier already had a percentage configured before this edit.
  // Used to prevent the secretariat from accidentally "clearing" a configured
  // percentage by emptying the input (leaving it blank would otherwise be a
  // silent no-op against the backend, which confuses users).
  const hadPercentageOnOpen = supplier.taxWithholdingPercentage != null;
  const hasExistingDocument = supplier.taxApprovalDocumentId != null;
  const hasNewFile = newDocumentFile != null;

  const handleSelectFile = (file: File | null) => {
    // Reject empty files before they enter the form: an empty docx is
    // ~always a download/save accident and uploading it gives a confusing
    // 400 from the backend. Better to refuse it up front.
    if (file && file.size === 0) {
      setNewDocumentFile(null);
      setError(t('suppliersManagement.dialog.emptyFileSelected', { name: file.name }));
      return;
    }
    setNewDocumentFile(file);
    setError(null);
  };

  const handleViewDocument = async () => {
    setError(null);
    setIsViewing(true);
    const failure = await openSupplierTaxApprovalDocument(supplier.id, currentUser?.token);
    if (failure) {
      setError(t('suppliersManagement.viewError') + (failure !== 'no_uri' ? `: ${failure}` : ''));
    }
    setIsViewing(false);
  };

  const handleSubmit = async () => {
    if (parsedPercentage !== null && (isNaN(parsedPercentage) || parsedPercentage < 0 || parsedPercentage > 100)) {
      setError(t('suppliersManagement.dialog.invalidPercentage'));
      return;
    }
    // Don't let the user blank-out a percentage that was already set on the supplier.
    // The backend treats null as "leave the existing value untouched", so clearing the
    // field would silently fail to update anything - surface the misuse explicitly instead.
    if (hadPercentageOnOpen && parsedPercentage === null) {
      setError(t('suppliersManagement.dialog.percentageRequiredForConfiguredSupplier'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Build the document payload up-front (if any) so it's part of the same atomic
      // PUT as the percentage / valid-until. Previously the dialog uploaded the document
      // in a separate POST first; if the subsequent PUT failed the supplier could be
      // left with a percentage but no document (or vice versa). The atomic endpoint
      // wraps both in a single DB transaction on the server.
      let document: DocumentDtoOut | undefined;
      if (hasNewFile && newDocumentFile) {
        const docs = await FileReaderHelper.fileToDocumentDtoOut(
          newDocumentFile,
          t('suppliersManagement.taxApprovalDocumentDescription'),
        );
        document = docs[0];
        if (!document?.contentBase64) {
          throw new Error(`Document base64 is empty for "${newDocumentFile.name}".`);
        }
      }

      // Overwrite semantics: every non-null value the user entered is sent and overwrites
      // the previous value on the supplier (the backend ignores null fields so an empty
      // valid-until on a legacy NULL-percentage supplier doesn't accidentally wipe other
      // fields). The new document, if any, replaces the previous approval document.
      const body: UpdateSupplierTaxApprovalDtoOut = {
        taxWithholdingPercentage: parsedPercentage,
        taxApprovalValidUntil: validUntil || null,
        document,
      };
      await putAuth<SupplierFullDtoIn, object, UpdateSupplierTaxApprovalDtoOut>(
        `${QUERY_URL}/${supplier.id}/tax-approval`, headers, {}, body,
      );

      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const msg = extractErrorMessage(err);
      setError(t('suppliersManagement.dialog.errorUpdating') + (msg ? `: ${msg}` : ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  // Status-driven banner shown at the top so the secretariat sees what's missing.
  const bannerText = (() => {
    switch (supplier.taxApprovalStatus) {
      case SupplierTaxApprovalStatus.PendingValidUntil:
        return t('suppliersManagement.dialog.approvalPendingValidUntilWarning');
      case SupplierTaxApprovalStatus.PendingDocument:
        return t('suppliersManagement.dialog.approvalPendingDocumentWarning');
      case SupplierTaxApprovalStatus.Expired:
        return t('suppliersManagement.dialog.approvalExpiredWarning');
      default:
        return null;
    }
  })();

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}
      title={`${t('suppliersManagement.dialog.taxWithholdingTitle')} - ${supplier.name}`}
    >
      <div className="flex flex-col gap-6 p-6 min-w-[560px]">
        {bannerText && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-amber-800 body-sm-semibold">{bannerText}</p>
          </div>
        )}

        <div className="flex gap-6">
          <LabeledInput
            label="suppliersManagement.taxWithholdingPercentage"
            type="number"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            placeholder="0"
            disabled={isSubmitting}
            width="100%"
          />
          <LabeledInput
            label="suppliersManagement.taxApprovalValidUntil"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            disabled={isSubmitting}
            width="100%"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="body-sm-regular text-(--font-heading)">
            {t('suppliersManagement.taxApprovalDocument')}
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <FileUpload
                placeholder={hasExistingDocument
                  ? t('suppliersManagement.replaceDocument')
                  : t('suppliersManagement.uploadDocument')}
                value={newDocumentFile}
                onChange={handleSelectFile}
                disabled={isSubmitting}
                accept=".pdf,.png,.jpg,.jpeg"
              />
            </div>
            {hasExistingDocument && !hasNewFile && (
              <Button variant="outline" onClick={handleViewDocument} disabled={isViewing}>
                {isViewing ? t('loading') : t('suppliersManagement.viewDocument')}
              </Button>
            )}
          </div>
          {hasExistingDocument && supplier.taxApprovalValidUntil && !hasNewFile && (
            <p className="body-sm-regular text-(--font-secondary)">
              {t('suppliersManagement.dialog.approvalValidUntilDate', {
                date: new Date(supplier.taxApprovalValidUntil).toLocaleDateString('he-IL'),
              })}
            </p>
          )}
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditSupplierTaxWithholdingDialog;
